<template>
  <div class="filter-popover-root">
    <LISAPopover
      v-model="showFilterPopover"
      class="filter-popover-surface"
      :placement="accent === 'event' ? 'bottom-start' : 'bottom-end'"
      :aria-label="title"
      @open="handlePopoverOpen"
    >
      <template #trigger="{ triggerAttrs }">
        <LISAButton v-bind="triggerAttrs" size="sm" icon="fas fa-filter" full-height>
          <span class="filter-button-label"
            >{{ t(buttonL10nKey, numberOf) }} [{{ numberOf }}x]</span
          >
        </LISAButton>
      </template>

      <template #default>
        <FilterList
          ref="filterList"
          :filter-config="filterConfig"
          :types="types"
          :site-id="siteId"
          :title="title"
          :accent="accent"
          :event-filters="eventFilters"
          :action-filters="actionFilters"
          @apply="applyFilters"
          @close="close"
        />
      </template>
    </LISAPopover>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";
import { nextTick, type PropType, ref } from "vue";
import { EABFilterType } from "@/graphql";
import { LISAButton, LISAPopover } from "@fi-solutions/lisa-ui";
import FilterList from "./FilterList.vue";
import { Filter, type FilterConfig, type FilterEventActionTypes } from "./FilterTypes";

const props = defineProps({
  filterConfig: {
    required: true,
    type: Array as PropType<FilterConfig[]>,
  },
  filters: {
    required: true,
    type: Array as PropType<Filter[]>,
  },
  eventFilters: {
    required: true,
    type: Array as PropType<Filter[]>,
  },
  actionFilters: {
    required: true,
    type: Array as PropType<Filter[]>,
  },
  filterType: {
    type: String as PropType<EABFilterType>,
    default: EABFilterType.AND,
  },
  buttonL10nKey: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  accent: {
    type: String as PropType<"event" | "action">,
    required: true,
  },
  numberOf: {
    type: Number,
    required: true,
  },
  types: {
    type: Array as PropType<FilterEventActionTypes[]>,
    default: () => [],
  },
  showArchive: {
    type: Boolean,
    default: false,
  },
  siteId: {
    type: Number,
    required: false,
    default: undefined,
  },
});

const emit = defineEmits<{
  apply: [Filter[], EABFilterType];
}>();
const filterList = ref<InstanceType<typeof FilterList>>();
const showFilterPopover = ref(false);
const pendingFocusFilter = ref<Filter>();
const { t } = useTranslation();

async function open(focusFilter?: Filter) {
  pendingFocusFilter.value = focusFilter;
  if (showFilterPopover.value) {
    await initializeFilterList(focusFilter);
    return;
  }
  showFilterPopover.value = true;
}

async function handlePopoverOpen() {
  await nextTick();
  await initializeFilterList(pendingFocusFilter.value);
  pendingFocusFilter.value = undefined;
}

async function initializeFilterList(focusFilter?: Filter) {
  await filterList.value?.initialize(props.filters, props.filterType, focusFilter);
}

function loadFilter(filter: Filter) {
  open(filter);
}

function close() {
  showFilterPopover.value = false;
}

function applyFilters(filters: Filter[], type: EABFilterType) {
  emit("apply", filters, type);
  close();
}

defineExpose({
  loadFilter,
  open,
});
</script>

<style lang="scss" scoped>
.filter-button-label,
.filter-button-count {
  flex: 0 0 auto;
  white-space: nowrap;
}

.filter-button-label {
  overflow: visible;
  text-overflow: clip;
}

:deep(.lisa-button__label) {
  max-width: none;
  overflow: visible;
  text-overflow: clip;
}

.filter-popover-root {
  height: 100%;

  :deep(.lisa-popover) {
    display: flex;
    height: 100%;
  }
}

:deep(.filter-popover-surface.lisa-popover__panel) {
  width: min(660px, calc(100vw - 4rem));
  max-width: calc(100vw - 1rem);
  overflow: hidden;
  padding: 0;
}

@container (max-width: 30rem) {
  .filter-button-count,
  .filter-button-label {
    display: none;
  }
}
</style>
